// src/pages/Campaigns/Sms/SmsManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Alert,
  Pagination,
  CircularProgress,
  Stack,
  Divider,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  Container,
  Tabs,
  Tab,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';

const SmsManagement = () => {
  const theme = useTheme();

  // General state
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // SMS Configuration State
  const [isSmsConfigured, setIsSmsConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [smsConfig, setSmsConfig] = useState({
    service_type: 'twilio',
    config: {
      account_sid: '',
      auth_token: '',
      from_number: ''
    }
  });

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsPagination, setCampaignsPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetailsOpen, setCampaignDetailsOpen] = useState(false);

  // Campaign creation modal
  const [createCampaignModal, setCreateCampaignModal] = useState({ open: false, useExisting: false });
  const [currentStep, setCurrentStep] = useState(0);
  const [campaignData, setCampaignData] = useState({
    campaign_name: '',
    scheduled_send_time: new Date(Date.now() + 3600000), // Default 1 hour from now
    selected_recipients: [],
    template_id: null,
    template_content: '',
    existing_campaign_id: null // NEW: For existing campaign reference
  });

  // NEW: Campaign search state
  const [campaignSearch, setCampaignSearch] = useState('');
  const [searchedCampaigns, setSearchedCampaigns] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Consented contacts
  const [consentedContacts, setConsentedContacts] = useState([]);
  const [contactsPagination, setContactsPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templatesPagination, setTemplatesPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [templateDialog, setTemplateDialog] = useState({ open: false, template: null });
  const [templateForm, setTemplateForm] = useState({ name: '', content: '' });
  const [templateCreationMode, setTemplateCreationMode] = useState('select'); // 'select' or 'create'

  // Messages state
  const [messages, setMessages] = useState([]);
  const [messagesPagination, setMessagesPagination] = useState({ page: 1, pages: 1, total: 0 });

  const campaignSteps = createCampaignModal.useExisting 
    ? ['Select Campaign', 'Campaign Details', 'Select Recipients', 'Choose Template']
    : ['Campaign Details', 'Select Recipients', 'Choose Template'];

  const normalizePhone = (phone) => {
    if (!phone) return '';
    return String(phone).replace(/[^0-9]/g, '');
  };

  // Check permissions and SMS config status
  useEffect(() => {
    const initialize = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('campaigns', 'view');
        const modifyPerm = await hasPermission('campaigns', 'add|edit');
        const deletePerm = await hasPermission('campaigns', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);

        if (viewPerm) {
          await checkSmsConfigStatus();
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        showSnackbar('An error occurred during initialization.', 'error');
      } finally {
        setCheckingPermissions(false);
      }
    };
    initialize();
  }, []);

  // Load data when SMS is configured
  useEffect(() => {
    if (canView && isSmsConfigured) {
      if (currentTab === 1) loadCampaigns();
      else if (currentTab === 2) loadTemplates(templatesPagination.page);
      else if (currentTab === 3) loadMessages(messagesPagination.page);
    }
  }, [canView, isSmsConfigured, currentTab]);

  // Re-apply pre-selections when contacts finish loading (for existing campaign flow)
  useEffect(() => {
      if (
        createCampaignModal.open && 
        createCampaignModal.useExisting && 
        currentStep === 1 && 
        consentedContacts.length > 0 && 
        campaignData.selected_recipients.length > 0
      ) {
        // 1. Create a Set of normalized numbers from the existing campaign data
        const targetNumbersNormalized = new Set(
          campaignData.selected_recipients.map(p => normalizePhone(p))
        );
        
        const newSelections = new Set();
        
        // 2. Loop through the VISIBLE contacts in the table
        consentedContacts.forEach(contact => {
          // Normalize the contact's phone number
          const contactPhoneNorm = normalizePhone(contact.phone);
          
          // 3. If the normalized contact number exists in the campaign list
          if (targetNumbersNormalized.has(contactPhoneNorm)) {
            // Add the EXACT string from the contact object (so the checkbox matches)
            newSelections.add(contact.phone);
          }
        });
        
        // 4. Update the selection state if matches were found
        if (newSelections.size > 0) {
          setSelectedContacts(prev => {
            // Create a combined set to ensure we don't lose manually clicked items if this runs twice
            const combined = new Set([...prev, ...newSelections]);
            // Only update state if size actually changes to prevent infinite re-renders
            if (combined.size !== prev.size) return combined;
            return prev;
          });
        }
      }
    }, [consentedContacts, currentStep, createCampaignModal.open, createCampaignModal.useExisting]);


  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getDefaultConfigForService = (serviceType) => {
    const defaults = {
      twilio: {
        account_sid: '',
        auth_token: '',
        from_number: ''
      },
      aws_sns: {
        access_key_id: '',
        secret_access_key: '',
        region: 'us-east-1'
      },
      messagebird: {
        api_key: '',
        originator: 'SMS'
      },
      vonage: {
        api_key: '',
        api_secret: '',
        from_number: ''
      }
    };
    return defaults[serviceType] || defaults.twilio;
  };

  const renderServiceConfigFields = () => {
    switch (smsConfig.service_type) {
      case 'twilio':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Twilio Account SID"
                name="account_sid"
                value={smsConfig.config.account_sid || ''}
                onChange={handleConfigChange}
                helperText="Your Twilio Account SID (starts with AC)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Twilio Auth Token"
                name="auth_token"
                type="password"
                value={smsConfig.config.auth_token || ''}
                onChange={handleConfigChange}
                helperText={isSmsConfigured ? "Leave unchanged to keep the existing token" : "Your Twilio Auth Token"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Twilio From Number"
                name="from_number"
                value={smsConfig.config.from_number || ''}
                onChange={handleConfigChange}
                placeholder="+1234567890"
                helperText="The phone number or sender ID to send from"
              />
            </Grid>
          </>
        );

      case 'aws_sns':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="AWS Access Key ID"
                name="access_key_id"
                value={smsConfig.config.access_key_id || ''}
                onChange={handleConfigChange}
                helperText="Your AWS IAM Access Key ID"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="AWS Secret Access Key"
                name="secret_access_key"
                type="password"
                value={smsConfig.config.secret_access_key || ''}
                onChange={handleConfigChange}
                helperText={isSmsConfigured ? "Leave unchanged to keep the existing key" : "Your AWS IAM Secret Access Key"}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>AWS Region</InputLabel>
                <Select
                  name="region"
                  value={smsConfig.config.region || 'us-east-1'}
                  label="AWS Region"
                  onChange={handleConfigChange}
                >
                  <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                  <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                  <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                  <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                  <MenuItem value="ap-northeast-1">Asia Pacific (Tokyo)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        );

      case 'messagebird':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="MessageBird API Key"
                name="api_key"
                type="password"
                value={smsConfig.config.api_key || ''}
                onChange={handleConfigChange}
                helperText={isSmsConfigured ? "Leave unchanged to keep the existing key" : "Your MessageBird API Key"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Originator (Sender ID)"
                name="originator"
                value={smsConfig.config.originator || 'SMS'}
                onChange={handleConfigChange}
                placeholder="YourBrand"
                helperText="The sender ID (up to 11 alphanumeric characters or phone number)"
              />
            </Grid>
          </>
        );

      case 'vonage':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Vonage API Key"
                name="api_key"
                value={smsConfig.config.api_key || ''}
                onChange={handleConfigChange}
                helperText="Your Vonage API Key (8 characters)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Vonage API Secret"
                name="api_secret"
                type="password"
                value={smsConfig.config.api_secret || ''}
                onChange={handleConfigChange}
                helperText={isSmsConfigured ? "Leave unchanged to keep the existing secret" : "Your Vonage API Secret"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="From Number"
                name="from_number"
                value={smsConfig.config.from_number || ''}
                onChange={handleConfigChange}
                placeholder="+1234567890"
                helperText="Alphanumeric sender ID (up to 11 chars) or phone number"
              />
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  const checkSmsConfigStatus = async () => {
    try {
      setConfigLoading(true);
      const response = await axiosInstance.get('/sms/service/status');
      if (response.data.success && response.data.data.is_configured) {
        setIsSmsConfigured(true);
        const config = response.data.data.configuration;
        const serviceType = response.data.data.service_type || 'twilio';
        
        // Mask sensitive fields based on service type
        let maskedConfig = { ...config };
        if (serviceType === 'twilio') {
          maskedConfig = {
            account_sid: config.account_sid || '',
            auth_token: '••••••••••••••••',
            from_number: config.from_number || ''
          };
        } else if (serviceType === 'aws_sns') {
          maskedConfig = {
            access_key_id: config.access_key_id || '',
            secret_access_key: '••••••••••••••••',
            region: config.region || 'us-east-1'
          };
        } else if (serviceType === 'messagebird') {
          maskedConfig = {
            api_key: '••••••••••••••••',
            originator: config.originator || 'SMS'
          };
        } else if (serviceType === 'vonage') {
          maskedConfig = {
            api_key: config.api_key || '',
            api_secret: '••••••••••••••••',
            from_number: config.from_number || 'Vonage'
          };
        }
        
        setSmsConfig({
          service_type: serviceType,
          config: maskedConfig
        });
        setCurrentTab(1); // Switch to campaigns tab
      } else {
        setIsSmsConfigured(false);
        setCurrentTab(0);
      }
    } catch (error) {
      showSnackbar('Could not verify SMS service status', 'error');
      setIsSmsConfigured(false);
      setCurrentTab(0);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setSmsConfig(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [name]: value
      }
    }));
  };

  const handleConfigSubmit = async () => {
    // Validate based on service type
    const config = smsConfig.config;
    let isValid = false;
    let maskedField = '';
    
    switch (smsConfig.service_type) {
      case 'twilio':
        isValid = config.account_sid && config.auth_token && config.from_number;
        maskedField = 'auth_token';
        if (!isValid) {
          showSnackbar('Account SID, Auth Token, and From Number are required for Twilio.', 'error');
          return;
        }
        break;
        
      case 'aws_sns':
        isValid = config.access_key_id && config.secret_access_key && config.region;
        maskedField = 'secret_access_key';
        if (!isValid) {
          showSnackbar('Access Key ID, Secret Access Key, and Region are required for AWS SNS.', 'error');
          return;
        }
        break;
        
      case 'messagebird':
        isValid = config.api_key && config.originator;
        maskedField = 'api_key';
        if (!isValid) {
          showSnackbar('API Key and Originator are required for MessageBird.', 'error');
          return;
        }
        break;
        
      case 'vonage':
        isValid = config.api_key && config.api_secret && config.from_number;
        maskedField = 'api_secret';
        if (!isValid) {
          showSnackbar('API Key, API Secret, and From Number are required for Vonage.', 'error');
          return;
        }
        break;
        
      default:
        showSnackbar('Please select a valid service provider.', 'error');
        return;
    }
    
    // Check if user is trying to update without providing new credentials
    if (isSmsConfigured && config[maskedField] === '••••••••••••••••') {
      showSnackbar(`To update, please provide the new ${maskedField.replace(/_/g, ' ')}.`, 'warning');
      return;
    }
    
    try {
      setLoading(true);
      await axiosInstance.post('/sms/service/configure', smsConfig);
      showSnackbar(`${smsConfig.service_type.toUpperCase()} service configured successfully!`, 'success');
      setIsSmsConfigured(true);
      setCurrentTab(1);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to configure SMS service', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CAMPAIGN MANAGEMENT ====================

  const loadCampaigns = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/sms/campaigns?page=${page}&per_page=20`);
      if (response.data.success) {
        setCampaigns(response.data.data);
        setCampaignsPagination(response.data.pagination);
      }
    } catch (error) {
      showSnackbar('Error loading campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignDetails = async (campaignId, page = 1) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/sms/campaigns/details?campaign_id=${campaignId}&page=${page}&per_page=50`);
      if (response.data.success) {
        setSelectedCampaign(response.data.data);
        setCampaignDetailsOpen(true);
      }
    } catch (error) {
      showSnackbar('Error loading campaign details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to cancel this campaign?')) return;

    try {
      setLoading(true);
      await axiosInstance.post(`/sms/campaigns/cancel?campaign_id=${campaignId}`);
      showSnackbar('Campaign cancelled successfully', 'success');
      loadCampaigns(campaignsPagination.page);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error cancelling campaign', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCampaignModal = (useExisting = false) => {
    setCreateCampaignModal({ open: true, useExisting });
    setCurrentStep(useExisting ? -1 : 0); // -1 for campaign selection step
    setCampaignData({
      campaign_name: '',
      scheduled_send_time: new Date(Date.now() + 3600000),
      selected_recipients: [],
      template_id: null,
      template_content: '',
      existing_campaign_id: null
    });
    setSelectedContacts(new Set());
    setTemplateCreationMode('select');
    setCampaignSearch('');
    setSearchedCampaigns([]);
  };

  const handleCloseCampaignModal = () => {
    setCreateCampaignModal({ open: false, useExisting: false });
    setCurrentStep(0);
    setSelectedContacts(new Set());
  };

  const handleCampaignNext = async () => {
    // Step -1: Campaign Selection (only for existing campaign flow)
    if (currentStep === -1) {
      if (!campaignData.existing_campaign_id) {
        showSnackbar('Please select a campaign', 'error');
        return;
      }
      // Campaign data already loaded in loadExistingCampaign
    }
    
    // Step 0: Campaign Details
    if (currentStep === 0) {
      if (!campaignData.campaign_name.trim()) {
        showSnackbar('Please enter campaign name', 'error');
        return;
      }
      if (!campaignData.scheduled_send_time) {
        showSnackbar('Please select scheduled send time', 'error');
        return;
      }
      if (campaignData.scheduled_send_time < new Date()) {
        showSnackbar('Scheduled time must be in the future', 'error');
        return;
      }
      
      console.log('=== STEP 0 -> STEP 1 TRANSITION ===');
      console.log('About to load contacts...');
      console.log('Current selectedContacts before load:', selectedContacts.size, Array.from(selectedContacts));
      console.log('Campaign selected_recipients:', campaignData.selected_recipients);
      
      // Load consented contacts first
      await loadConsentedContacts(1);
      
      console.log('Contacts loaded. Contacts count:', consentedContacts.length);
      
      // IMPORTANT: If this is existing campaign flow, re-apply pre-selections after contacts loaded
      if (createCampaignModal.useExisting && campaignData.selected_recipients.length > 0) {
        console.log('=== RE-APPLYING IN NEXT HANDLER ===');
        console.log('Recipients to select:', campaignData.selected_recipients);
        const selectionsSet = new Set(campaignData.selected_recipients);
        console.log('Created Set with size:', selectionsSet.size);
        setSelectedContacts(selectionsSet);
        console.log('Set selectedContacts called');
      }
    } 
    // Step 1: Select Recipients
    else if (currentStep === 1) {
      if (selectedContacts.size === 0) {
        showSnackbar('Please select at least one recipient', 'error');
        return;
      }
      // Save selected recipients to campaign data
      setCampaignData(prev => ({
        ...prev,
        selected_recipients: Array.from(selectedContacts)
      }));
      // Load templates
      await loadTemplates(1);
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleCampaignBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.template_id && templateCreationMode === 'select') {
      showSnackbar('Please select a template', 'error');
      return;
    }

    if (templateCreationMode === 'create' && !campaignData.template_content.trim()) {
      showSnackbar('Please enter message content', 'error');
      return;
    }

    try {
      setLoading(true);

      let templateId = campaignData.template_id;

      // Create new template if needed
      if (templateCreationMode === 'create') {
        const templateResponse = await axiosInstance.post('/sms/templates', {
          name: `Template for ${campaignData.campaign_name}`,
          content: campaignData.template_content
        });
        
        if (templateResponse.data.success) {
          templateId = templateResponse.data.data.id;
        } else {
          throw new Error('Failed to create template');
        }
      }

      // Create campaign
      const campaignPayload = {
        campaign_name: campaignData.campaign_name,
        template_id: templateId,
        scheduled_send_time: campaignData.scheduled_send_time.toISOString(),
        recipient_phone_numbers: campaignData.selected_recipients
      };

      const response = await axiosInstance.post('/sms/campaigns', campaignPayload);

      if (response.data.success) {
        showSnackbar('Campaign created successfully and will be sent at scheduled time!', 'success');
        handleCloseCampaignModal();
        loadCampaigns(1);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating campaign', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== CONSENTED CONTACTS ====================

  const loadConsentedContacts = async (page = 1) => {
    try {
      setContactsLoading(true);
      const response = await axiosInstance.get(`/sms/consent/contacts?page=${page}&per_page=50`);
      if (response.data.success) {
        // Backend now provides last_campaign_name and last_sent_time
        setConsentedContacts(response.data.data);
        setContactsPagination(response.data.pagination);
      }
    } catch (error) {
      showSnackbar('Error loading consented contacts', 'error');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleSelectContact = (phone) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phone)) {
        newSet.delete(phone);
      } else {
        newSet.add(phone);
      }
      return newSet;
    });
  };

  const handleSelectAllContacts = () => {
    if (selectedContacts.size === consentedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(consentedContacts.map(c => c.phone)));
    }
  };

  // ==================== TEMPLATES ====================

  const loadTemplates = async (page = 1) => {
    try {
      const response = await axiosInstance.get(`/sms/templates?page=${page}&per_page=20`);
      if (response.data.success) {
        setTemplates(response.data.data || []);
        setTemplatesPagination(response.data.pagination);
      }
    } catch (error) {
      showSnackbar('Error loading templates', 'error');
    }
  };

  const handleTemplateSubmit = async () => {
    try {
      setLoading(true);

      if (templateDialog.template) {
        await axiosInstance.put(`/sms/templates?template_id=${templateDialog.template.id}`, templateForm);
        showSnackbar('Template updated successfully', 'success');
      } else {
        await axiosInstance.post('/sms/templates', templateForm);
        showSnackbar('Template created successfully', 'success');
      }

      setTemplateDialog({ open: false, template: null });
      setTemplateForm({ name: '', content: '' });
      loadTemplates(templatesPagination.page);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axiosInstance.delete(`/sms/templates?template_id=${templateId}`);
      showSnackbar('Template deleted successfully', 'success');
      loadTemplates(templatesPagination.page);
    } catch (error) {
      showSnackbar('Error deleting template', 'error');
    }
  };

  // ==================== MESSAGES ====================

  const loadMessages = async (page = 1) => {
    try {
      const response = await axiosInstance.get(`/sms/messages?page=${page}&per_page=50`);
      if (response.data.success) {
        setMessages(response.data.data);
        setMessagesPagination(response.data.pagination);
      }
    } catch (error) {
      showSnackbar('Error loading messages', 'error');
    }
  };

  // ==================== CAMPAIGN SEARCH ====================

  const searchCampaigns = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchedCampaigns([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axiosInstance.get(`/sms/campaigns?page=1&per_page=20`);
      if (response.data.success) {
        const filtered = response.data.data.filter(campaign =>
          campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setSearchedCampaigns(filtered);
      }
    } catch (error) {
      showSnackbar('Error searching campaigns', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const loadExistingCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/sms/campaigns/details?campaign_id=${campaignId}&page=1&per_page=1000`);
      
      if (response.data.success) {
        const campaign = response.data.data;
        
        // Extract recipient phone numbers
        const recipientPhones = campaign.recipients.map(r => r.phone_number);
        
        console.log('=== LOADING EXISTING CAMPAIGN ===');
        console.log('Campaign ID:', campaign.id);
        console.log('Total Recipients:', campaign.total_recipients);
        console.log('Recipient Phones:', recipientPhones);
        
        // Pre-fill campaign data
        setCampaignData({
          campaign_name: campaign.campaign_name + ' (Copy)',
          scheduled_send_time: new Date(Date.now() + 3600000), // New schedule time
          selected_recipients: recipientPhones,
          template_id: campaign.template_id,
          template_content: campaign.template?.content || '',
          existing_campaign_id: campaign.id
        });

        // Pre-select recipients using Set (avoids duplicates)
        const preSelectedSet = new Set(recipientPhones);
        console.log('Setting selectedContacts Set:', preSelectedSet);
        setSelectedContacts(preSelectedSet);

        showSnackbar(`Loaded campaign "${campaign.campaign_name}" with ${campaign.total_recipients} recipients`, 'success');
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
      showSnackbar('Error loading campaign', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      scheduled: 'info',
      processing: 'warning',
      completed: 'success',
      cancelled: 'default',
      failed: 'error',
      pending: 'warning',
      sent: 'info',
      delivered: 'success'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (checkingPermissions || configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1" color="textSecondary">
            {checkingPermissions ? 'Checking permissions...' : 'Verifying SMS configuration...'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Access Denied</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You don't have permission to view SMS Management. Please contact your administrator.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" component="h1" fontWeight={700} color="textPrimary">
          SMS Campaign Management
        </Typography>
        {canModify && isSmsConfigured && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCampaignModal(true)}
              size="large"
            >
              Use Existing Campaign
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => handleOpenCampaignModal(false)}
              size="large"
            >
              Create New Campaign
            </Button>
          </Stack>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="SMS Service Config" />
          <Tab label="Campaigns" disabled={!isSmsConfigured} />
          <Tab label="Templates" disabled={!isSmsConfigured} />
          <Tab label="Messages History" disabled={!isSmsConfigured} />
        </Tabs>
      </Box>

      {/* Tab 0: SMS Service Config */}
      {currentTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>SMS Service Configuration</Typography>
          {!isSmsConfigured ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              The SMS service is not configured. Please select a provider and enter your credentials below to enable all SMS features.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {smsConfig.service_type.toUpperCase()} is currently configured and active.
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                You can update the configuration below or switch to a different provider.
              </Typography>
            </Alert>
          )}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Service Provider</InputLabel>
                    <Select
                      value={smsConfig.service_type}
                      label="Service Provider"
                      onChange={(e) => {
                        const newServiceType = e.target.value;
                        setSmsConfig(prev => ({
                          service_type: newServiceType,
                          config: getDefaultConfigForService(newServiceType)
                        }));
                      }}
                    >
                      <MenuItem value="twilio">Twilio</MenuItem>
                      <MenuItem value="aws_sns">AWS SNS</MenuItem>
                      <MenuItem value="messagebird">MessageBird</MenuItem>
                      <MenuItem value="vonage">Vonage (Nexmo)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {renderServiceConfigFields()}
                
                {canModify && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleConfigSubmit}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Configuration'}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab 1: Campaigns List */}
      {currentTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>SMS Campaigns</Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : campaigns.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <MessageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No campaigns found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first SMS campaign to get started
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Campaign Name</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Scheduled Time</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Recipients</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Sent/Failed</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                      <TableCell><Typography variant="subtitle2" fontWeight={600}>Actions</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {campaign.campaign_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(campaign.scheduled_send_time)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={campaign.total_recipients} size="small" />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip 
                              label={`Sent: ${campaign.sent_count}`} 
                              size="small" 
                              color="success" 
                              variant="outlined"
                            />
                            {campaign.failed_count > 0 && (
                              <Chip 
                                label={`Failed: ${campaign.failed_count}`} 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.status}
                            color={getStatusColor(campaign.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => loadCampaignDetails(campaign.id)}
                              title="View Details"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            {(campaign.status === 'scheduled' || campaign.status === 'draft') && canDelete && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelCampaign(campaign.id)}
                                title="Cancel Campaign"
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={campaignsPagination.pages}
                  page={campaignsPagination.page}
                  onChange={(e, page) => loadCampaigns(page)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Tab 2: Templates */}
      {currentTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">SMS Templates</Typography>
            {canModify && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => {
                  setTemplateForm({ name: '', content: '' });
                  setTemplateDialog({ open: true, template: null });
                }}
              >
                Create Template
              </Button>
            )}
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Content Preview</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id} hover>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>
                      {template.content.length > 100
                        ? `${template.content.substring(0, 100)}...`
                        : template.content}
                    </TableCell>
                    <TableCell>
                      {new Date(template.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {canModify && (
                        <IconButton
                          onClick={() => {
                            setTemplateForm({ name: template.name, content: template.content });
                            setTemplateDialog({ open: true, template });
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          onClick={() => handleDeleteTemplate(template.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={templatesPagination.pages}
              page={templatesPagination.page}
              onChange={(e, page) => loadTemplates(page)}
              color="primary"
            />
          </Box>
        </Box>
      )}

      {/* Tab 3: Messages History */}
      {currentTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            SMS Messages History
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Message Preview</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id} hover>
                    <TableCell>
                      {message.campaign_name || 'Direct Send'}
                    </TableCell>
                    <TableCell>{message.phone_number}</TableCell>
                    <TableCell>
                      {message.message_content.length > 50
                        ? `${message.message_content.substring(0, 50)}...`
                        : message.message_content}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={message.status}
                        color={getStatusColor(message.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {message.sent_at
                        ? formatDateTime(message.sent_at)
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={messagesPagination.pages}
              page={messagesPagination.page}
              onChange={(e, page) => loadMessages(page)}
              color="primary"
            />
          </Box>
        </Box>
      )}

      {/* Create Campaign Modal */}
      <Dialog
        open={createCampaignModal.open}
        onClose={handleCloseCampaignModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Create SMS Campaign
            </Typography>
            <IconButton onClick={handleCloseCampaignModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Stepper activeStep={createCampaignModal.useExisting ? currentStep + 1 : currentStep} sx={{ mb: 4 }}>
            {campaignSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step -1: Select Existing Campaign (only for useExisting flow) */}
          {currentStep === -1 && createCampaignModal.useExisting && (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Step 1:</strong> Search and select an existing campaign to use as a template.
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="Search Campaign by Name"
                value={campaignSearch}
                onChange={(e) => {
                  setCampaignSearch(e.target.value);
                  searchCampaigns(e.target.value);
                }}
                placeholder="Type campaign name (e.g., abc)"
                autoFocus
                helperText="Start typing to search for campaigns"
              />

              {searchLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {campaignSearch && searchedCampaigns.length > 0 && (
                <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell>Campaign Name</TableCell>
                        <TableCell>Recipients</TableCell>
                        <TableCell>Scheduled Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchedCampaigns.map((campaign) => (
                        <TableRow 
                          key={campaign.id}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            bgcolor: campaignData.existing_campaign_id === campaign.id 
                              ? alpha(theme.palette.primary.main, 0.1) 
                              : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {campaign.campaign_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={campaign.total_recipients} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDateTime(campaign.scheduled_send_time)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={campaign.status}
                              color={getStatusColor(campaign.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant={campaignData.existing_campaign_id === campaign.id ? "contained" : "outlined"}
                              size="small"
                              onClick={() => loadExistingCampaign(campaign.id)}
                              disabled={loading}
                            >
                              {campaignData.existing_campaign_id === campaign.id ? 'Selected' : 'Select'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {campaignSearch && !searchLoading && searchedCampaigns.length === 0 && (
                <Alert severity="warning">
                  No campaigns found matching "{campaignSearch}". Try a different search term.
                </Alert>
              )}

              {campaignData.existing_campaign_id && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Campaign selected! You can now modify the details in the next steps.
                  </Typography>
                </Alert>
              )}
            </Stack>
          )}

          {/* Step 0: Campaign Details */}
          {currentStep === 0 && (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Step 1:</strong> Enter campaign name and schedule the send time.
                </Typography>
              </Alert>
              <TextField
                fullWidth
                label="Campaign Name"
                value={campaignData.campaign_name}
                onChange={(e) => setCampaignData(prev => ({ ...prev, campaign_name: e.target.value }))}
                placeholder="e.g., Spring Sale 2025"
                required
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Scheduled Send Time"
                  value={campaignData.scheduled_send_time}
                  onChange={(newValue) => setCampaignData(prev => ({ ...prev, scheduled_send_time: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                  minDateTime={new Date()}
                />
              </LocalizationProvider>
            </Stack>
          )}

          {/* Step 1: Select Recipients */}
          {currentStep === 1 && (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Step {createCampaignModal.useExisting ? '3' : '2'}:</strong> Select recipients from your consented contacts.
                  {createCampaignModal.useExisting && selectedContacts.size > 0 && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'success.main' }}>
                      ✓ {selectedContacts.size} contacts pre-selected from original campaign
                    </Typography>
                  )}
                </Typography>
              </Alert>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Selected: {selectedContacts.size} / {consentedContacts.length}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAllContacts}
                >
                  {selectedContacts.size === consentedContacts.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              {contactsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : consentedContacts.length === 0 ? (
                <Alert severity="warning">
                  No consented contacts found. Please add contacts with consent first.
                </Alert>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedContacts.size === consentedContacts.length && consentedContacts.length > 0}
                              indeterminate={selectedContacts.size > 0 && selectedContacts.size < consentedContacts.length}
                              onChange={handleSelectAllContacts}
                            />
                          </TableCell>
                          <TableCell>Phone Number</TableCell>
                          <TableCell>Consent Date</TableCell>
                          <TableCell>Last Campaign</TableCell>
                          <TableCell>Last Sent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {consentedContacts.map((contact, index) => {
                          const isSelected = selectedContacts.has(contact.phone);
                          
                          // Debug logging for first 3 contacts only to avoid spam
                          if (index < 3) {
                            console.log(`Contact ${index}:`, {
                              phone: contact.phone,
                              isInSet: selectedContacts.has(contact.phone),
                              isSelected,
                              selectedContactsSize: selectedContacts.size,
                              selectedContactsList: Array.from(selectedContacts)
                            });
                          }
                          
                          return (
                            <TableRow
                              key={contact.phone}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'inherit'
                              }}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectContact(contact.phone);
                                  }}
                                />
                              </TableCell>
                              <TableCell onClick={() => handleSelectContact(contact.phone)}>
                                <Typography variant="body2" fontWeight={isSelected ? 600 : 500}>
                                  {contact.phone}
                                </Typography>
                              </TableCell>
                              <TableCell onClick={() => handleSelectContact(contact.phone)}>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(contact.consent_date).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                              <TableCell onClick={() => handleSelectContact(contact.phone)}>
                                <Typography variant="body2" color="text.secondary">
                                  {contact.last_campaign_name || 'Never sent'}
                                </Typography>
                              </TableCell>
                              <TableCell onClick={() => handleSelectContact(contact.phone)}>
                                <Typography variant="body2" color="text.secondary">
                                  {contact.last_sent_time 
                                    ? formatDateTime(contact.last_sent_time)
                                    : 'N/A'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={contactsPagination.pages}
                      page={contactsPagination.page}
                      onChange={(e, page) => loadConsentedContacts(page)}
                      size="small"
                    />
                  </Box>
                </>
              )}
            </Stack>
          )}

          {/* Step 2: Choose Template */}
          {currentStep === 2 && (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Step 3:</strong> Select an existing template or create a new message.
                </Typography>
              </Alert>

              <FormControl component="fieldset">
                <FormLabel component="legend">Template Option</FormLabel>
                <RadioGroup
                  value={templateCreationMode}
                  onChange={(e) => setTemplateCreationMode(e.target.value)}
                >
                  <FormControlLabel value="select" control={<Radio />} label="Select Existing Template" />
                  <FormControlLabel value="create" control={<Radio />} label="Create New Message" />
                </RadioGroup>
              </FormControl>

              {templateCreationMode === 'select' ? (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Select Template</InputLabel>
                    <Select
                      value={campaignData.template_id || ''}
                      label="Select Template"
                      onChange={(e) => {
                        const selectedTemplate = templates.find(t => t.id === e.target.value);
                        setCampaignData(prev => ({
                          ...prev,
                          template_id: e.target.value,
                          template_content: selectedTemplate?.content || ''
                        }));
                      }}
                    >
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {campaignData.template_content && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Message Preview
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {campaignData.template_content}
                          </Typography>
                        </Paper>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <TextField
                  fullWidth
                  label="Message Content"
                  multiline
                  rows={6}
                  value={campaignData.template_content}
                  onChange={(e) => setCampaignData(prev => ({ ...prev, template_content: e.target.value }))}
                  placeholder="Enter your SMS message here..."
                  required
                />
              )}

              <Divider />

              <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Campaign Summary
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Campaign Name:
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {campaignData.campaign_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled Time:
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {campaignData.scheduled_send_time.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Recipients:
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {campaignData.selected_recipients.length} contacts
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseCampaignModal} disabled={loading}>
            Cancel
          </Button>
          {currentStep > 0 && (
            <Button onClick={handleCampaignBack} disabled={loading}>
              Back
            </Button>
          )}
          {currentStep < campaignSteps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleCampaignNext}
              disabled={loading}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateCampaign}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ScheduleIcon />}
            >
              {loading ? 'Creating...' : 'Schedule Campaign'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Campaign Details Modal */}
      <Dialog
        open={campaignDetailsOpen}
        onClose={() => setCampaignDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Campaign Details
            </Typography>
            <IconButton onClick={() => setCampaignDetailsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {selectedCampaign && (
            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Campaign Name</Typography>
                      <Typography variant="body1" fontWeight={600}>{selectedCampaign.campaign_name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip label={selectedCampaign.status} color={getStatusColor(selectedCampaign.status)} size="small" />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Scheduled Time</Typography>
                      <Typography variant="body1">{formatDateTime(selectedCampaign.scheduled_send_time)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Template</Typography>
                      <Typography variant="body1">{selectedCampaign.template_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Total Recipients</Typography>
                      <Typography variant="h6" fontWeight={600}>{selectedCampaign.total_recipients}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Sent</Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">{selectedCampaign.sent_count}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Failed</Typography>
                      <Typography variant="h6" fontWeight={600} color="error.main">{selectedCampaign.failed_count}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Typography variant="subtitle1" fontWeight={600}>Recipients</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Sent At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCampaign.recipients?.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell>{recipient.phone_number}</TableCell>
                        <TableCell>
                          <Chip
                            label={recipient.is_sent ? 'Sent' : recipient.is_failed ? 'Failed' : 'Pending'}
                            color={recipient.is_sent ? 'success' : recipient.is_failed ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {recipient.sent_at ? formatDateTime(recipient.sent_at) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedCampaign.recipients_pagination && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={selectedCampaign.recipients_pagination.pages}
                    page={selectedCampaign.recipients_pagination.page}
                    onChange={(e, page) => loadCampaignDetails(selectedCampaign.id, page)}
                    size="small"
                  />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setCampaignDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog
        open={templateDialog.open}
        onClose={() => setTemplateDialog({ open: false, template: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {templateDialog.template ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={templateForm.name}
            onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Message Content"
            multiline
            rows={4}
            value={templateForm.content}
            onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
            margin="normal"
            required
            helperText="Enter your message content here"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog({ open: false, template: null })}>
            Cancel
          </Button>
          {canModify && (
            <Button
              onClick={handleTemplateSubmit}
              variant="contained"
              disabled={loading || !templateForm.name || !templateForm.content}
            >
              {loading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SmsManagement;